<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  invoices: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  payingInvoiceId: { type: [Number, String], default: null },
});

const emit = defineEmits(['view', 'pay']);

const { t } = useI18n();

const formatDate = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
};

const statusClass = status => {
  if (status === 'paid') return 'bg-n-teal-3 text-n-teal-11';
  if (status === 'overdue') return 'bg-n-ruby-3 text-n-ruby-11';
  if (status === 'deferred') return 'bg-n-amber-3 text-n-amber-11';
  return 'bg-n-alpha-2 text-n-slate-12';
};

const billingReasonClass = reason => {
  if (reason === 'initial_plan') return 'bg-n-blue-3 text-n-blue-11';
  if (reason === 'plan_renewal') return 'bg-n-teal-3 text-n-teal-11';
  if (reason === 'seat_addition') return 'bg-n-amber-3 text-n-amber-11';
  if (reason === 'ai_credit_topup') return 'bg-n-ruby-3 text-n-ruby-11';
  return 'bg-n-alpha-2 text-n-slate-12';
};

const isPayable = invoice =>
  invoice.payable || ['pending', 'overdue'].includes(invoice.status);

const visibleInvoices = computed(() =>
  props.invoices.filter(invoice => invoice.status !== 'cancelled')
);

const billingReasonLabel = reason => {
  const key = reason || 'plan_renewal';
  const path = `GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_BILLING_REASON.${key}`;
  const label = t(path);
  return label === path ? key.replace(/_/g, ' ') : label;
};
</script>

<template>
  <section class="flex flex-col gap-4">
    <p v-if="isLoading" class="mb-0 text-sm text-n-slate-11">
      {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICES_LOADING') }}
    </p>

    <p v-else-if="!visibleInvoices.length" class="mb-0 text-sm text-n-slate-11">
      {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICES_EMPTY') }}
    </p>

    <div
      v-else
      class="overflow-x-auto rounded-lg border border-solid border-n-weak"
    >
      <table class="w-full min-w-[640px] text-left text-sm">
        <thead>
          <tr
            class="border-b border-solid border-n-weak bg-n-alpha-1 text-xs font-semibold uppercase text-n-slate-11"
          >
            <th class="px-4 py-3">
              {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_COL_ISSUED') }}
            </th>
            <th class="px-4 py-3">
              {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_NUMBER') }}
            </th>
            <th class="px-4 py-3">
              {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_COL_TYPE') }}
            </th>
            <th class="px-4 py-3">
              {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_COL_STATUS') }}
            </th>
            <th class="px-4 py-3">
              {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_COL_TOTAL') }}
            </th>
            <th class="px-4 py-3 text-end">
              {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_COL_ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr
            v-for="invoice in visibleInvoices"
            :key="invoice.id"
            class="hover:bg-n-alpha-1"
          >
            <td class="px-4 py-3 text-n-slate-11">
              {{ formatDate(invoice.issued_at) }}
            </td>
            <td class="px-4 py-3 tabular-nums text-n-slate-12">
              {{ invoice.invoice_number || invoice.id }}
            </td>
            <td class="px-4 py-3">
              <span
                class="inline-flex rounded-md px-2 py-0.5 text-xs font-medium"
                :class="billingReasonClass(invoice.billing_reason)"
              >
                {{ billingReasonLabel(invoice.billing_reason) }}
              </span>
            </td>
            <td class="px-4 py-3">
              <span
                class="inline-flex rounded-md px-2 py-0.5 text-xs font-medium capitalize"
                :class="statusClass(invoice.status)"
              >
                {{ invoice.status }}
              </span>
            </td>
            <td class="px-4 py-3 tabular-nums font-medium text-n-slate-12">
              <span v-if="invoice.currency_symbol">
                {{ invoice.currency_symbol }}
              </span>
              {{ Number(invoice.total).toFixed(2) }}
              <span class="ms-1 text-n-slate-11">{{
                invoice.currency_code
              }}</span>
            </td>
            <td class="px-4 py-3">
              <div class="flex justify-end gap-2">
                <Button
                  variant="faded"
                  color="slate"
                  size="sm"
                  :label="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.INVOICE_VIEW')"
                  @click="emit('view', invoice)"
                />
                <Button
                  v-if="isPayable(invoice)"
                  size="sm"
                  :label="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.PAY_INVOICE')"
                  :is-loading="payingInvoiceId === invoice.id"
                  @click="emit('pay', invoice)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>
